/**
 * CNPJ utilities — mask + Receita-Federal check-digit validator.
 *
 * Mirrors the Java `DocumentCompositeValidator` used by Keycloak for the
 * candidate realm (CPF). We re-implement here because the recruiter SPA
 * doesn't go through KC for company CNPJs — Core API stores them after
 * the BFF accepts the form.
 */

const CNPJ_MASKED = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;

/** "12345678000195" → "12.345.678/0001-95". Partial input is masked
 *  progressively so the field reflects the user's typing without forcing
 *  them to enter the punctuation. */
export function formatCnpj(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 14);
  const len = digits.length;
  if (len <= 2) return digits;
  if (len <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (len <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (len <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** True when the input matches `00.000.000/0000-00` AND the two check
 *  digits compute correctly per Receita Federal's modulo-11 algorithm.
 *  All-same-digit CNPJs (e.g. "11.111.111/1111-11") are rejected even
 *  though their check digits arithmetically pass — none are issued. */
export function isValidCnpj(value: string): boolean {
  if (!CNPJ_MASKED.test(value)) return false;

  const digits = value.replace(/\D/g, '').split('').map(Number);

  if (digits.every((d) => d === digits[0])) return false;

  // First check digit — weights cycle 5..2, then 9..2 (12 digits → 12 weights).
  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const firstSum = firstWeights.reduce((acc, w, i) => acc + w * digits[i], 0);
  const firstRemainder = firstSum % 11;
  const firstCheck = firstRemainder < 2 ? 0 : 11 - firstRemainder;
  if (digits[12] !== firstCheck) return false;

  // Second check digit — same shape, weights cycle 6..2, then 9..2 (13 digits).
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondSum = secondWeights.reduce((acc, w, i) => acc + w * digits[i], 0);
  const secondRemainder = secondSum % 11;
  const secondCheck = secondRemainder < 2 ? 0 : 11 - secondRemainder;
  return digits[13] === secondCheck;
}
